import React, { useContext } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  Paper,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import currency from "currency.js";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import bkstApi from "../../api";
import { UserContext } from "../../context/UserContext";
import SendReceipt from "../../order/component/SendReceipt";
import { isPOSMode, logOffPosStaff } from "../util";

const POS_SIDE_W = 220;
const POS_SIDE_H = 340;

function PosSideAction(props) {
  const { label, icon, onClick, to, bg, compact = false } = props;
  return (
    <ButtonBase
      component={to ? RouterLink : "button"}
      to={to}
      onClick={onClick}
      sx={{
        width: "100%",
        maxWidth: compact ? 400 : POS_SIDE_W,
        ...(compact
          ? { minHeight: 88, flexDirection: "row", gap: 2, p: 2 }
          : {
              width: POS_SIDE_W,
              height: POS_SIDE_H,
              flexDirection: "column",
              gap: 2.5,
              p: 3,
            }),
        borderRadius: "12px",
        justifyContent: "center",
        backgroundColor: bg,
        color: bg === "primary.main" ? "primary.contrastText" : "text.primary",
        "&:hover": { filter: "brightness(0.95)" },
      }}
    >
      <Box
        sx={{
          width: compact ? 52 : 96,
          height: compact ? 52 : 96,
          borderRadius: "50%",
          bgcolor: "#FFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: 1,
        }}
      >
        {icon}
      </Box>
      <Typography
        variant={compact ? "h6" : "h5"}
        sx={{ fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

/**
 * Payment outcome UI. Layout is driven by remaining balance + fulfillment type:
 * - to-go with balance due → warning + collect-payment only (no receipt chrome)
 * - otherwise → success chrome + New Sale / Done (+ optional Add Payment for non-togo balance)
 *
 * Pass `order` when available (uses order.payment.due + order.fulfillment.type);
 * otherwise `result.remainingDue` + `result.fulfillmentType`.
 */
export default function PaymentSuccessScreen(props) {
  const {
    open,
    onClose,
    result = {},
    order,
    title,
    newSaleTo,
    onDone,
    onCollectPayment,
    collectPaymentLoading = false,
    collectPaymentError = "",
  } = props;
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const remainingDue =
    result && Object.prototype.hasOwnProperty.call(result, "remainingDue")
      ? +result.remainingDue || 0
      : +order?.payment?.due || 0;
  const fulfillmentType = order?.fulfillment?.type || result.fulfillmentType || "";
  const isTogoBalanceDue = fulfillmentType === "togo" && remainingDue > 0;

  const hasReceiptPrinter = Boolean(user.config?.receiptPrinterId);
  const heading = title || (result.quoteId ? "Invoice Sent" : "Payment Received");
  const posMode = isPOSMode(user);
  const primaryLabel = newSaleTo ? "New Sale" : "Done";
  const hasPrimary = Boolean(newSaleTo || onDone);

  const printReceipt = () => {
    bkstApi
      .post(
        `/my/printer/${user.config.receiptPrinterId}/job?orderId=${result.orderId || ""}&paymentIntentId=${result.paymentIntentId || ""}`
      )
      .then(() => {});
  };

  const printPackingSlip = () => {
    bkstApi
      .post(`/my/printer/${user.config.receiptPrinterId}/packing-slip?orderId=${result.orderId}`)
      .then(() => {});
  };

  if (isTogoBalanceDue) {
    return (
      <Dialog
        fullScreen
        open={open}
        scroll="paper"
        onClose={() => {}}
        PaperProps={{ sx: { backgroundColor: "#FAFAFA" } }}
      >
        <DialogContent sx={{ px: 2 }}>
          <Box
            pt={6}
            pb={4}
            sx={{
              width: "400px",
              maxWidth: "100%",
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: "50%",
                backgroundColor: "rgba(211, 47, 47, 0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2.5,
              }}
            >
              <WarningAmberOutlinedIcon sx={{ fontSize: "2.75rem", color: "error.main" }} />
            </Box>

            <Typography variant="overline" color="error" sx={{ letterSpacing: "0.12em" }}>
              Balance Due
            </Typography>

            <Typography variant="h2" color="error" sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
              {currency(remainingDue).format()}
            </Typography>

            <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 320, mb: 1 }}>
              Partial payment received. Collect balance due to complete the sale.
            </Typography>

            {onCollectPayment && (
              <Button
                fullWidth
                size="large"
                color="primary"
                variant="contained"
                onClick={onCollectPayment}
                disabled={collectPaymentLoading}
                sx={{ mt: 3, py: 1.5, fontSize: "1.1rem" }}
              >
                {collectPaymentLoading ? "Loading…" : "Take Another Payment"}
              </Button>
            )}
            {collectPaymentError && (
              <Alert severity="error" sx={{ justifyContent: "center", mt: 1.5, width: "100%" }}>
                {collectPaymentError}
              </Alert>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const lockPos = () => logOffPosStaff(navigate);

  const posPrimarySide = hasPrimary ? (
    <PosSideAction
      label={primaryLabel}
      to={newSaleTo}
      onClick={newSaleTo ? undefined : onDone}
      bg="primary.main"
      icon={<PointOfSaleOutlinedIcon sx={{ fontSize: "3.5rem", color: "primary.main" }} />}
    />
  ) : null;

  const posLockSide = (
    <PosSideAction
      label="Lock"
      onClick={lockPos}
      bg="#EEEEEE"
      icon={<LockOutlinedIcon sx={{ fontSize: "3.5rem", color: "grey.800" }} />}
    />
  );

  const actionStack = (
    <>
      {!posMode &&
        hasPrimary &&
        (newSaleTo ? (
          <Button
            fullWidth
            size="large"
            color="primary"
            variant="contained"
            component={RouterLink}
            to={newSaleTo}
          >
            New Sale
          </Button>
        ) : (
          <Button fullWidth size="large" color="primary" variant="contained" onClick={onDone}>
            Done
          </Button>
        ))}
      {remainingDue > 0 && onCollectPayment && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body1" color="textSecondary" textAlign="center">
            Balance Due {currency(remainingDue).format()}
          </Typography>
          <Button
            fullWidth
            size="large"
            color="secondary"
            variant="outlined"
            onClick={onCollectPayment}
            disabled={collectPaymentLoading}
          >
            {collectPaymentLoading ? "Loading…" : "Add Payment"}
          </Button>
        </Box>
      )}
      {collectPaymentError && (
        <Alert severity="error" sx={{ justifyContent: "center" }}>
          {collectPaymentError}
        </Alert>
      )}
      {(result.orderId || result.paymentIntentId) && (
        <>
          <Box>
            <Typography
              variant="overline"
              color="textSecondary"
              sx={{ display: "block", mb: 0.75, letterSpacing: "0.08em" }}
            >
              Send Receipt
            </Typography>
            <Paper elevation={0} sx={{ backgroundColor: "#F7F7F7", borderRadius: "8px" }}>
              <Box p={1.5} sx={{ display: "flex", gap: "0.75rem" }}>
                <SendReceipt
                  fullWidth
                  orderId={result.orderId}
                  paymentIntentId={result.paymentIntentId}
                  mode="email"
                  email={result.email}
                >
                  <Button
                    fullWidth
                    size="large"
                    color="secondary"
                    variant="contained"
                    disableElevation
                  >
                    Email
                  </Button>
                </SendReceipt>
                <SendReceipt
                  fullWidth
                  orderId={result.orderId}
                  paymentIntentId={result.paymentIntentId}
                  mode="sms"
                  phone={result.phone}
                >
                  <Button
                    fullWidth
                    size="large"
                    color="secondary"
                    variant="contained"
                    disableElevation
                  >
                    Text
                  </Button>
                </SendReceipt>
              </Box>
            </Paper>
          </Box>
          <Box>
            <Typography
              variant="overline"
              color="textSecondary"
              sx={{ display: "block", mb: 0.75, letterSpacing: "0.08em" }}
            >
              Print
            </Typography>
            <Paper elevation={0} sx={{ backgroundColor: "#F7F7F7", borderRadius: "8px" }}>
              <Box p={1.5} sx={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <Box sx={{ display: "flex", gap: "0.75rem" }}>
                  <Button
                    fullWidth
                    size="large"
                    color="secondary"
                    variant="contained"
                    disableElevation
                    onClick={() => printReceipt()}
                    disabled={!hasReceiptPrinter}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Receipt
                  </Button>
                  {result.orderId && (
                    <Button
                      fullWidth
                      size="large"
                      color="secondary"
                      variant="contained"
                      disableElevation
                      onClick={() => printPackingSlip()}
                      disabled={!hasReceiptPrinter}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      Packing Slip
                    </Button>
                  )}
                </Box>
                {!hasReceiptPrinter && (
                  <Typography variant="caption" color="textSecondary" sx={{ px: 0.5 }}>
                    Requires a receipt printer
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        </>
      )}
      {result.orderId && (
        <Box mt={3}>
          <Button
            fullWidth
            size="large"
            color="secondary"
            variant="outlined"
            component={RouterLink}
            to={`/order/${result.orderId}`}
          >
            View Order
          </Button>
        </Box>
      )}
      {!result.orderId && result.paymentIntentId && (
        <Button
          fullWidth
          size="large"
          color="secondary"
          variant="outlined"
          to={`/redirect?from=pi&id=${result.paymentIntentId}&to=order`}
          component={RouterLink}
        >
          View Order
        </Button>
      )}
      {result.quoteId && (
        <Button
          fullWidth
          size="large"
          color="secondary"
          variant="outlined"
          component={RouterLink}
          to={`/quote/${result.quoteId}`}
        >
          View Invoice
        </Button>
      )}
    </>
  );

  const receiptPanel = (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        borderRadius: "12px",
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
        ...(posMode ? { minHeight: { md: POS_SIDE_H } } : {}),
      }}
    >
      {actionStack}
    </Paper>
  );

  return (
    <Dialog fullScreen open={open} scroll="paper" onClose={onClose}>
      <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
        <Box
          sx={
            posMode
              ? {
                  display: { xs: "block", md: "grid" },
                  gridTemplateColumns: { md: "minmax(0, 1fr) minmax(320px, 440px) minmax(0, 1fr)" },
                  columnGap: { md: 4 },
                  alignItems: "start",
                }
              : { display: "flex", flexDirection: "column", alignItems: "center" }
          }
        >
          <Box
            sx={{
              gridColumn: posMode ? { md: "1 / -1" } : "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2,
              width: "100%",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: "5rem", color: "#16a085" }} />
            <Typography variant="h3" color="textSecondary" gutterBottom>
              {heading}
            </Typography>
            {result.cash && (
              <Typography variant="body1" color="textSecondary" textAlign="center">
                Cash Tendered: {currency(result.cash.tendered).format()} | Change Due:{" "}
                {currency(result.cash.change).format()}
              </Typography>
            )}
          </Box>

          {posMode && hasPrimary && (
            <Box sx={{ display: { xs: "none", md: "flex" }, justifyContent: "flex-end", pr: 2 }}>
              {posPrimarySide}
            </Box>
          )}

          <Box sx={{ width: "100%", maxWidth: 400, mx: "auto" }}>
            {receiptPanel}
            {posMode && (
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  flexDirection: "column",
                  gap: 1.5,
                  mt: 2,
                }}
              >
                {hasPrimary && (
                  <PosSideAction
                    label={primaryLabel}
                    to={newSaleTo}
                    onClick={newSaleTo ? undefined : onDone}
                    bg="primary.main"
                    compact
                    icon={
                      <PointOfSaleOutlinedIcon sx={{ fontSize: "2rem", color: "primary.main" }} />
                    }
                  />
                )}
                <PosSideAction
                  label="Lock"
                  onClick={lockPos}
                  bg="#EEEEEE"
                  compact
                  icon={<LockOutlinedIcon sx={{ fontSize: "2rem", color: "grey.800" }} />}
                />
              </Box>
            )}
          </Box>

          {posMode && (
            <Box sx={{ display: { xs: "none", md: "flex" }, justifyContent: "flex-start", pl: 2 }}>
              {posLockSide}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
